import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeftRight,
  Check,
  CheckCheck,
  X,
  Ban,
  AlertCircle,
  RefreshCw,
  Loader2,
  Link2,
  FileText,
  Building2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DashboardLayout } from '../components/DashboardLayout';
import { BankLogo } from '../components/BankLogo';
import { InvoicePdfButton } from '../components/InvoicePdfButton';
import { useEntityStore } from '../store';
import { useBankingFilter } from '../contexts/BankingFilterContext';
import { matchingAPI, invoicesAPI, transactionsAPI, recurringCostMatchingAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn, formatCurrency } from '../lib/utils';
import { ManualMatchDialog } from '../components/ManualMatchDialog';
import { ReconcileWithoutInvoiceDialog } from '../components/ReconcileWithoutInvoiceDialog';
import { AcceptResidualDialog } from '../components/AcceptResidualDialog';
import { HighConfidenceBulkConfirmDialog } from '../components/HighConfidenceBulkConfirmDialog';
import { AutoMatchChainControl } from '../components/AutoMatchChainControl';
import { RecurringCostCoverageCard } from '../components/admin/RecurringCostCoverageCard';
import { ReceiptPaidReconciliationPanel } from '../components/admin/ReceiptPaidReconciliationPanel';
import { ReconciliationMemoryCard } from '../components/admin/ReconciliationMemoryCard';
import { buildEntityLookup } from '../lib/entities';
import { format } from 'date-fns';

var PAGE_SIZE = 20;
// Cap on unreconciled-transaction rows rendered for manual resolution. The
// stat card shows the true total; a capped list is flagged in the UI.
var UNRECONCILED_ROW_CAP = 100;

var confidenceConfig = {
  high: { label: 'High Confidence', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', score: 95 },
  medium: { label: 'Medium Confidence', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', score: 70 },
  low: { label: 'Low Confidence', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', score: 40 },
};

// Evidence tier: which auto-confirm arm (if any) is behind a suggestion,
// highest-authority first -- BR OS wise-exact identity (services/
// bros_evidence.py::wise_exact_identity) outranks an accounting-reconciled
// pin (services/accounting_reconciled_evidence.py, match.accounting_evidence
// .pins_this_candidate), which outranks a suggestion that exists purely on
// its match_score with no corroborating evidence. Distinct from the fuller
// BR OS/reference-match badges already rendered below -- this is the single
// small signal for "why does this rank here, and would an arm auto-confirm
// it or does it wait for a human".
function evidenceTier(match) {
  if (match.bros_evidence && match.bros_evidence.identity_exact) {
    return { label: 'BR OS wise-exact', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' };
  }
  if (match.accounting_evidence && match.accounting_evidence.pins_this_candidate) {
    return { label: 'Accounting pin', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' };
  }
  return { label: 'Plain score', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800/40 dark:text-slate-400' };
}

// One row per candidate invoice inside a grouped transaction card. Each
// candidate keeps its own Confirm/Reject -- confirming one auto-dismisses
// the siblings server-side (routes/matching.py confirm_match), which is why
// this row never needs to know about the other candidates. selected/
// onToggleSelect back the select-all -> bulk confirm bar above the list
// (POST /matches/bulk-confirm-selected) -- a row with no match (the
// not-found-reference / stale-invoice early returns below) never renders a
// checkbox, since there is no match id to select.
function CandidateRow({ candidate, transaction, entityById, onConfirm, onReject, onAcceptResidual, selected, onToggleSelect }) {
  var match = candidate.match;
  var invoice = candidate.invoice;

  // Referenced-invoice-not-found: the payer's reference cited an invoice
  // number that doesn't resolve to any invoice in this transaction's
  // entity -- surfaced explicitly rather than silently falling back to
  // amount-only scoring (see routes/matching.py::_not_found_reference_tokens).
  if (!match && candidate.not_found_reference) {
    return (
      <div
        className="border-t py-3 flex items-center gap-3 text-sm text-muted-foreground first:border-t-0"
        data-testid={'not-found-reference-' + candidate.not_found_reference}
      >
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
        <span>
          Referenced invoice not found: <span className="font-mono">{candidate.not_found_reference}</span>
        </span>
      </div>
    );
  }

  var config = confidenceConfig[match.confidence] || confidenceConfig.low;
  var matchScore = match.match_score != null ? match.match_score : config.score;
  var isReferenceMatch = !!match.reference_match;
  var brOsCorroboration = match.br_os_corroboration;
  var brosEvidence = match.bros_evidence;

  // Stale candidate: the invoice this suggestion pointed at was deleted or
  // changed since the match was created.
  if (!invoice) {
    return (
      <div className="border-t py-3 flex items-center gap-3 text-sm text-muted-foreground first:border-t-0">
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
        <span>Stale candidate: referenced invoice no longer available.</span>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto text-red-600"
          onClick={function () { onReject(match.id); }}
          data-testid={'reject-match-' + match.id}
        >
          <X className="h-3 w-3 mr-1" /> Dismiss
        </Button>
      </div>
    );
  }

  var invEntity = entityById ? entityById(invoice.entity_id) : null;
  var supplierName = invoice.supplier_client_name;

  // Reference match + amount mismatch: shown, never swallowed. Diff is
  // computed only when both sides carry the same currency -- a
  // cross-currency reference match already shows both native amounts in
  // the grid below, no invented conversion.
  var showAmountDiff = false;
  var txAmount = null;
  var amountDiff = null;
  if (isReferenceMatch && transaction && transaction.currency && invoice.currency === transaction.currency) {
    txAmount = Math.abs(Number(transaction.amount));
    var invAmount = Number(invoice.amount);
    if (Number.isFinite(txAmount) && Number.isFinite(invAmount)) {
      amountDiff = txAmount - invAmount;
      showAmountDiff = Math.round(amountDiff * 100) !== 0;
    }
  }

  return (
    <div className="border-t py-4 first:border-t-0 first:pt-0" data-testid={'candidate-row-' + match.id}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {onToggleSelect && (
            <Checkbox
              checked={!!selected}
              onCheckedChange={function () { onToggleSelect(match.id); }}
              aria-label={'Select match ' + match.id + ' for bulk confirm'}
              data-testid={'select-match-' + match.id}
            />
          )}
          <Badge className={config.color}>{config.label}</Badge>
          {isReferenceMatch && (
            <Badge
              className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
              data-testid={'reference-match-badge-' + match.id}
            >
              Reference match
            </Badge>
          )}
          {brOsCorroboration === 'paid' || brOsCorroboration === 'partial' ? (
            <Badge
              className="bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400"
              data-testid={'br-os-corroboration-badge-' + match.id}
            >
              BR OS: marked {brOsCorroboration === 'partial' ? 'partially paid' : 'paid'}
            </Badge>
          ) : null}
          {brosEvidence ? (
            <Badge
              className={
                brosEvidence.identity_exact
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400'
              }
              data-testid={'bros-evidence-badge-' + match.id}
              title={[
                brosEvidence.payment_reference,
                brosEvidence.transfer_number && ('Transfer #' + brosEvidence.transfer_number),
                brosEvidence.payer_name,
                brosEvidence.payment_date,
              ].filter(Boolean).join(' · ')}
            >
              {brosEvidence.identity_exact
                ? 'BR OS: ' + brosEvidence.source + ' exact match'
                : 'BR OS: ' + brosEvidence.source + ' evidence'}
            </Badge>
          ) : null}
          {invoice.matching_status === 'partially_matched' && (
            <Badge
              className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
              data-testid={'partially-matched-badge-' + match.id}
              title="Confirming this candidate adds to what's already been allocated to this invoice -- it does not replace it."
            >
              Invoice partially matched
            </Badge>
          )}
          <span className="text-xs text-muted-foreground font-mono">{matchScore}pts</span>
          <Badge
            variant="outline"
            className={cn('text-[10px] px-1.5 py-0 font-normal', evidenceTier(match).color)}
            data-testid={'evidence-tier-' + match.id}
            title="Which auto-confirm arm (if any) is behind this suggestion"
          >
            {evidenceTier(match).label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <InvoicePdfButton invoice={invoice} variant="button" />
          {invoice.matching_status === 'partially_matched' && onAcceptResidual && (
            <Button
              variant="outline"
              size="sm"
              onClick={function () { onAcceptResidual(invoice); }}
              data-testid={'accept-residual-' + invoice.id}
            >
              Accept residual
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={function () { onReject(match.id); }}
            className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            data-testid={'reject-match-' + match.id}
          >
            <X className="h-4 w-4 mr-1" /> Reject
          </Button>
          <Button
            size="sm"
            onClick={function () { onConfirm(match.id); }}
            className="bg-green-600 hover:bg-green-700"
            data-testid={'confirm-match-' + match.id}
          >
            <Check className="h-4 w-4 mr-1" /> Confirm
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm bg-muted/40 rounded-lg p-3">
        <div>
          <div className="text-muted-foreground text-xs">Invoice #</div>
          <div className="font-mono truncate">{invoice.invoice_number}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">Amount</div>
          <div className="font-mono font-medium">{formatCurrency(invoice.amount, invoice.currency)}</div>
          {showAmountDiff && (
            <div className="font-mono text-xs text-amber-600 dark:text-amber-500" data-testid={'amount-diff-' + match.id}>
              Txn {formatCurrency(txAmount, invoice.currency)} &middot; Diff {formatCurrency(Math.abs(amountDiff), invoice.currency)}
            </div>
          )}
          {invoice.matching_status === 'partially_matched' && (
            <div className="font-mono text-xs text-amber-600 dark:text-amber-500" data-testid={'allocation-progress-' + match.id}>
              {formatCurrency(invoice.amount_paid || 0, invoice.currency)} allocated &middot; {formatCurrency(
                Math.max(0, Number(invoice.amount || 0) - Number(invoice.amount_paid || 0)), invoice.currency
              )} remaining
            </div>
          )}
        </div>
        <div>
          <div className="text-muted-foreground text-xs">Entity</div>
          <div className="truncate" data-testid={'match-inv-entity-' + match.id}>
            {invEntity ? invEntity.name : <span className="text-muted-foreground/40">Unattributed</span>}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">{invoice.type === 'inbound' ? 'Supplier' : 'Client'}</div>
          <div className="truncate" data-testid={'match-inv-counterparty-' + match.id}>
            {supplierName || <span className="text-muted-foreground/50 italic">No supplier extracted</span>}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">Due Date</div>
          <div>{invoice.due_date ? format(new Date(invoice.due_date), 'dd MMM') : '\u2014'}</div>
        </div>
      </div>
    </div>
  );
}

// One card per bank transaction, listing every candidate invoice (grouped
// server-side by get_match_details) so the same transaction never renders as
// several separate cards.
function TransactionMatchCard({ group, entityById, onConfirm, onReject, onRejectAll, onManualMatch, onAcceptResidual, highlighted, cardRef, selectedMatchIds, onToggleSelect }) {
  var transaction = group.transaction;
  var bankAccount = group.bank_account;
  var candidates = group.candidates || [];

  // Fully stale group: the transaction is gone AND every candidate's invoice
  // is gone too -- nothing left to review, only a dismiss action makes sense.
  if (!transaction && candidates.every(function (c) { return !c.invoice; })) {
    return (
      <div className="border rounded-xl p-4 bg-muted/30 text-muted-foreground text-sm flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
        <span>Stale match: referenced transaction or invoice no longer available.</span>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto text-red-600"
          onClick={function () { onRejectAll(group.transaction_id); }}
          data-testid={'reject-all-' + group.transaction_id}
        >
          <X className="h-3 w-3 mr-1" /> Dismiss
        </Button>
      </div>
    );
  }

  var bankName = bankAccount ? bankAccount.bank_name : null;
  var txEntity = transaction && entityById ? entityById(transaction.entity_id) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      ref={cardRef}
      className={"border rounded-xl p-6 hover:shadow-lg transition-all bg-card" + (highlighted ? " ring-2 ring-primary" : "")}
    >
      {/* Transaction header -- one per card, shared by every candidate below */}
      <div className="flex items-start justify-between mb-2 pb-4 border-b gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {bankName ? (
            <BankLogo bankName={bankName} size="sm" />
          ) : (
            <Building2 className="h-5 w-5 text-primary" />
          )}
          <div>
            <div className="font-medium text-sm">{bankName || 'Bank Transaction'}</div>
            <div className="text-xs text-muted-foreground">
              {transaction && transaction.date ? format(new Date(transaction.date), 'dd MMM yyyy') : '\u2014'}
              {' \u00b7 '}
              <span className="font-mono">
                {transaction ? formatCurrency(transaction.amount, transaction.currency) : '\u2014'}
              </span>
            </div>
          </div>
        </div>
        <div className="text-sm text-right space-y-0.5">
          <div>
            <span className="text-muted-foreground mr-1">Entity</span>
            <span data-testid="match-tx-entity">
              {txEntity ? txEntity.name : <span className="text-muted-foreground/40">Unattributed</span>}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground mr-1">Counterparty</span>
            <span data-testid="match-tx-counterparty">
              {transaction && transaction.counterparty ? transaction.counterparty : <span className="text-muted-foreground/40">\u2014</span>}
            </span>
          </div>
          {transaction && transaction.reference && (
            <div className="font-mono text-xs text-muted-foreground">
              Ref: {transaction && transaction.reference ? transaction.reference : '-'}
            </div>
          )}
          {transaction && transaction.wise_transfer_id && (
            <div className="font-mono text-[10px] text-muted-foreground/60" title="Wise's own internal transfer id, kept separate from the payer-entered reference above">
              Wise ID: {transaction.wise_transfer_id}
            </div>
          )}
        </div>
      </div>

      <div>
        {candidates.map(function (candidate) {
          return (
            <CandidateRow
              key={candidate.match ? candidate.match.id : 'not-found-' + candidate.not_found_reference}
              candidate={candidate}
              transaction={transaction}
              entityById={entityById}
              onConfirm={onConfirm}
              onReject={onReject}
              onAcceptResidual={onAcceptResidual}
              selected={candidate.match ? selectedMatchIds && selectedMatchIds.has(candidate.match.id) : false}
              onToggleSelect={candidate.match ? onToggleSelect : undefined}
            />
          );
        })}
      </div>

      {transaction && (
        <div className="flex justify-end items-center gap-2 mt-1 pt-3 border-t">
          {/* A live suggestion (however wrong) used to make this the only
              row type with NO path to Match manually / group mode -- the
              Unreconciled Transactions table below only lists transactions
              with zero suggestions. Reachable here too now, regardless of
              candidate count. */}
          <Button
            variant="outline"
            size="sm"
            onClick={function () { onManualMatch(transaction); }}
            data-testid={'manual-match-btn-suggested-' + group.transaction_id}
          >
            <Link2 className="h-3.5 w-3.5 mr-1" /> Match manually
          </Button>
          {candidates.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-red-600"
              onClick={function () { onRejectAll(group.transaction_id); }}
              data-testid={'reject-all-' + group.transaction_id}
            >
              <Ban className="h-3.5 w-3.5 mr-1" /> Reject all
            </Button>
          )}
        </div>
      )}
    </motion.div>
  );
}

export function ReconciliationPage({ embedded, scope } = {}) {
  var entityStore = useEntityStore();
  var selectedEntity = entityStore.selectedEntity;
  var entities = entityStore.entities;
  // Same UUID-or-slug resolver as TransactionsPage -- every live bank-feed
  // writer stamps entity_id as a slug, so a UUID-only lookup here would
  // silently render blank for the transaction side of every match card.
  var entityById = useMemo(function () { return buildEntityLookup(entities); }, [entities]);
  // Scope bounds the tab: "entities" excludes personal movements (even at All
  // Entities), "personal" includes only personal. Defaults to entities.
  var scopeParam = scope === 'personal' ? 'personal' : 'entities';

  // Drill-through: highlight a specific transaction's match card
  var bankingFilter = useBankingFilter();
  var highlightTxId = bankingFilter.transactionFilter;
  var highlightRef = useRef(null);
  // Transactions-tab entity/bank filters, carried here via URL query params
  // (BankingPage's tab switch preserves location.search) so a filter set on
  // the Transactions tab keeps narrowing the transaction side here too.
  var txEntityFilter = bankingFilter.txEntityFilter || 'all';
  var txBankFilter = bankingFilter.txBankFilter || 'all';

  // Match details (paginated, server-joined)
  var [matchDetails, setMatchDetails] = useState([]);
  var [matchTotal, setMatchTotal] = useState(0);
  var [page, setPage] = useState(1);

  // Select-all -> bulk confirm (POST /matches/bulk-confirm-selected): the
  // operator's own selection, page-scoped -- a stale id from a page/filter
  // change is pruned in the effect below rather than left dangling.
  var [selectedMatchIds, setSelectedMatchIds] = useState(function () { return new Set(); });
  var [bulkConfirming, setBulkConfirming] = useState(false);

  // Stats: separate lightweight fetches for the stat cards
  var [statsReconciled, setStatsReconciled] = useState(0);
  var [statsUnreconciled, setStatsUnreconciled] = useState(0);
  var [statsPendingInvoices, setStatsPendingInvoices] = useState(0);

  // Unreconciled transactions that have no auto-suggestion: the rows that
  // otherwise have no on-tab resolution. Capped; the total drives the stat.
  var [unreconciledRows, setUnreconciledRows] = useState([]);
  var [manualTarget, setManualTarget] = useState(null);
  var [reconcileTarget, setReconcileTarget] = useState(null);
  var [residualTarget, setResidualTarget] = useState(null);

  var [isLoading, setIsLoading] = useState(true);
  var [fetchError, setFetchError] = useState(null);
  // Auto-matching entity scope: same "type" value the old sync endpoint
  // took (ent.type, not the raw selectedEntity id) -- AutoMatchChainControl
  // manages its own running/polling state, so no local isMatching flag.
  var autoMatchEntity = selectedEntity !== 'all' ? entities.find(function (e) { return e.id === selectedEntity; }) : null;
  var autoMatchEntityId = autoMatchEntity ? autoMatchEntity.type : undefined;

  // High Confidence bulk-confirm (Commit 2): candidate list drives both the
  // button's count and the preview dialog, so they can never disagree.
  var [highConfidenceCandidates, setHighConfidenceCandidates] = useState([]);
  var [showHighConfidenceDialog, setShowHighConfidenceDialog] = useState(false);

  // Suggestions-tab-local entity filter (distinct from the sidebar's global
  // entity scope, same override pattern as txEntityFilter above) -- narrows
  // the entity-grouped suggestion list without touching the sidebar
  // selection or the other two tabs.
  var [suggestionsEntityFilter, setSuggestionsEntityFilter] = useState('all');

  // Recurring-costs tab count for its tab label. A separate lightweight
  // fetch rather than lifting RecurringCostCoverageCard's own state -- that
  // card manages its own grid/preview/apply refresh cycle independently and
  // does not need to be touched for this.
  var [recurringCostsCount, setRecurringCostsCount] = useState(0);

  // Receipt-suggestions tab count for its tab label -- the standing
  // "Suggested (pending review)" bucket ReceiptPaidReconciliationPanel
  // itself already reads (services/receipt_paid_reconciliation.py::
  // count_suggested_pending_review via GET /admin/diagnostics/receipt-paid-
  // reconciliation/awaiting-review-count), same independent-lightweight-
  // fetch pattern as recurringCostsCount above: this page only needs the
  // number for the tab label, not the panel's own internal state, so a
  // failure here is non-critical -- the panel's own fetch still surfaces
  // its own error if the endpoint is down.
  var [receiptSuggestedCount, setReceiptSuggestedCount] = useState(0);

  var fetchData = useCallback(async function () {
    try {
      setFetchError(null);
      var entityTypeCode = selectedEntity !== 'all' ? selectedEntity : undefined;
      // Transactions-tab entity filter (incl. "Unattributed") overrides the
      // sidebar-derived entity for the unreconciled-transactions query only
      // -- the suggested-match list stays scoped to the sidebar entity since
      // a match's transaction and invoice can belong to different entities.
      var effectiveTxEntityId = txEntityFilter !== 'all' ? txEntityFilter : entityTypeCode;
      // Suggestions-tab entity filter overrides the sidebar-derived entity
      // for the suggestions query only, same override shape as above.
      var effectiveSuggestionsEntityId = suggestionsEntityFilter !== 'all' ? suggestionsEntityFilter : entityTypeCode;

      // Fetch match details (paginated, server-joined) and stats in parallel.
      // The details endpoint does its own transaction/invoice lookup, so the
      // "empty list despite non-zero count" bug cannot recur.
      var [detailsRes, reconciledTxRes, txRes, invRes, suggestedRes, highConfidenceRes] = await Promise.all([
        matchingAPI.getDetails({ status: 'suggested', entity_id: effectiveSuggestionsEntityId, scope: scopeParam, page: page, page_size: PAGE_SIZE }),
        // Reconciled count only, page_size:1 -- the backend's is_reconciled
        // field is the single source of truth for both match systems
        // (services.reconciliation_predicates.is_transaction_reconciled: set
        // by ordinary invoice matching AND by a confirmed statement_payout
        // match), so this total already includes statement-reconciled
        // transactions without a second query shape.
        transactionsAPI.getAll({
          entity_id: effectiveTxEntityId,
          bank_name: txBankFilter !== 'all' ? txBankFilter : undefined,
          scope: scopeParam,
          is_reconciled: true,
          page_size: 1,
        }),
        // One fetch gives both the unreconciled rows and their total count.
        transactionsAPI.getAll({
          entity_id: effectiveTxEntityId,
          bank_name: txBankFilter !== 'all' ? txBankFilter : undefined,
          scope: scopeParam,
          is_reconciled: false,
          page_size: UNRECONCILED_ROW_CAP,
        }),
        // Filter pending server-side on payment_status (the normalized invoice
        // response has no "status" field; the old client-side i.status filter
        // silently evaluated to 0 for every invoice). limit is the endpoint cap;
        // a 500 result means the count is a floor, surfaced as "500+" below.
        invoicesAPI.getAll({ entity_id: entityTypeCode, scope: scopeParam, payment_status: 'pending', limit: 500 }),
        // Transaction ids that already carry a suggestion, so we do not list a
        // transaction as "needs manual resolution" when a suggestion exists.
        matchingAPI.getAll('suggested'),
        // High Confidence bulk-confirm candidate list (Commit 2) -- drives the
        // "Confirm all high-confidence (N)" button count and its preview dialog.
        matchingAPI.highConfidencePreview(entityTypeCode),
      ]);

      setMatchDetails(detailsRes.data.matches || []);
      setMatchTotal(detailsRes.data.total || 0);
      setStatsReconciled(reconciledTxRes.data.total || 0);
      setStatsUnreconciled(txRes.data.total || 0);
      setStatsPendingInvoices(invRes.data.length);
      setHighConfidenceCandidates(highConfidenceRes.data.candidates || []);

      var suggestedTxIds = new Set((suggestedRes.data || []).map(function (m) { return m.transaction_id; }));
      var rows = (txRes.data.transactions || []).filter(function (t) {
        return !suggestedTxIds.has(t.id);
      });
      setUnreconciledRows(rows);
    } catch (error) {
      setFetchError(getErrorMessage(error));
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [selectedEntity, page, scopeParam, txEntityFilter, txBankFilter, suggestionsEntityFilter]);

  // Reset to page 1 when the entity scope changes. Suggested-match totals are
  // now entity-scoped, so a narrower entity has fewer pages; without this a
  // user on page 3 of "All Entities" would be stranded on an out-of-range,
  // empty page after selecting an entity with only one page of matches.
  useEffect(function () {
    setPage(1);
  }, [selectedEntity, suggestionsEntityFilter]);

  useEffect(function () {
    if (entities.length > 0 || selectedEntity === 'all') {
      setIsLoading(true);
      fetchData();
    }
  }, [fetchData, entities]);

  // Recurring-costs tab count: independent of the main fetchData cycle
  // above (not entity/page-scoped, same census RecurringCostCoverageCard
  // reads for its own display) -- fetched once on mount so a failure here
  // cannot take down the Suggestions/stats data those depend on.
  useEffect(function () {
    var cancelled = false;
    recurringCostMatchingAPI.getCensus().then(function (res) {
      if (cancelled) return;
      var census = res.data;
      var bucketCounts = (census && census.ready && census.bucket_counts) || {};
      setRecurringCostsCount(bucketCounts.expected_unmatched || 0);
    }).catch(function () {
      // Non-critical secondary metric -- RecurringCostCoverageCard's own
      // fetch already surfaces a toast if the census endpoint is down.
    });
    return function () { cancelled = true; };
  }, []);

  // Receipt-suggestions tab count: same standing population as
  // ReceiptPaidReconciliationPanel's own internal badge, fetched
  // independently here purely for the tab label -- see the state
  // declaration above for why this doesn't lift the panel's own state.
  useEffect(function () {
    var cancelled = false;
    matchingAPI.receiptPaidAwaitingReviewCount().then(function (res) {
      if (cancelled) return;
      setReceiptSuggestedCount((res.data && res.data.count) || 0);
    }).catch(function () {
      // Non-critical secondary metric -- the panel's own fetch already
      // surfaces its own error state if the endpoint is down.
    });
    return function () { cancelled = true; };
  }, []);

  // Scroll to highlighted match card after data loads
  useEffect(function () {
    if (highlightTxId && !isLoading && highlightRef.current) {
      if (typeof highlightRef.current.scrollIntoView === 'function') {
        highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightTxId, isLoading, matchDetails]);

  var handleAutoMatchRunCompleted = async function (result) {
    if (result && result.duplicate_candidate_transactions > 0) {
      toast.info(
        result.duplicate_candidate_transactions + ' transaction' +
        (result.duplicate_candidate_transactions !== 1 ? 's' : '') + ' need a manual pick between multiple candidates'
      );
    }
    setPage(1);
    await fetchData();
  };

  var handleConfirm = async function (matchId) {
    try {
      var response = await matchingAPI.confirm(matchId);
      // Partial-payment slice: a confirm can settle the invoice only
      // PARTIALLY (matching_status rides along on the response, see
      // routes/matching.py::confirm_match) -- say so, rather than
      // implying the invoice is fully done.
      if (response.data && response.data.matching_status === 'partially_matched') {
        toast.success('Match confirmed — invoice partially matched, balance remaining');
      } else {
        toast.success('Match confirmed');
      }
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  var handleReject = async function (matchId) {
    try {
      await matchingAPI.reject(matchId);
      toast.success('Match rejected');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  var handleRejectAll = async function (transactionId) {
    try {
      var result = await matchingAPI.rejectAllForTransaction(transactionId);
      toast.success('Rejected ' + (result.data.count || 0) + ' candidate(s)');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Every match id currently rendered on this page -- "select all" only
  // ever selects what the operator can actually see, never a phantom
  // select-all-matching-filter across unloaded pages.
  var visibleMatchIds = useMemo(function () {
    var ids = [];
    matchDetails.forEach(function (group) {
      (group.candidates || []).forEach(function (c) {
        if (c.match) ids.push(c.match.id);
      });
    });
    return ids;
  }, [matchDetails]);

  var handleToggleSelect = function (matchId) {
    setSelectedMatchIds(function (prev) {
      var next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId); else next.add(matchId);
      return next;
    });
  };

  var allVisibleSelected = visibleMatchIds.length > 0 && visibleMatchIds.every(function (id) { return selectedMatchIds.has(id); });

  var handleToggleSelectAll = function () {
    setSelectedMatchIds(function () {
      return allVisibleSelected ? new Set() : new Set(visibleMatchIds);
    });
  };

  var handleBulkConfirmSelected = async function () {
    var ids = Array.from(selectedMatchIds);
    if (ids.length === 0) return;
    if (!window.confirm(
      'Confirm ' + ids.length + ' selected match' + (ids.length !== 1 ? 'es' : '') + '? ' +
      'Each goes through the same checks as an individual Confirm -- a match no longer ' +
      'confirmable (already claimed elsewhere, invoice still pending review, etc.) is ' +
      'skipped with a reason, not silently dropped, and the rest of the batch still proceeds.'
    )) return;
    setBulkConfirming(true);
    try {
      var res = await matchingAPI.bulkConfirmSelected(ids);
      var data = res.data || {};
      var confirmedCount = data.confirmed_count || 0;
      var skippedCount = data.skipped_count || 0;
      if (confirmedCount > 0) {
        var msg = 'Confirmed ' + confirmedCount + ' match' + (confirmedCount !== 1 ? 'es' : '');
        if (skippedCount > 0) msg += ' (' + skippedCount + ' skipped, see below)';
        toast.success(msg);
      }
      if (skippedCount > 0) {
        (data.skipped || []).forEach(function (s) {
          toast.error('Skipped ' + s.match_id + ': ' + s.reason);
        });
      }
      setSelectedMatchIds(new Set());
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBulkConfirming(false);
    }
  };

  // Prune the selection to whatever is actually rendered -- a page/filter
  // change (or a refetch after some other action) must never leave a
  // dangling selected id the operator can no longer see or that no longer
  // exists.
  useEffect(function () {
    setSelectedMatchIds(function (prev) {
      if (prev.size === 0) return prev;
      var visible = new Set(visibleMatchIds);
      var next = new Set();
      var changed = false;
      prev.forEach(function (id) {
        if (visible.has(id)) next.add(id); else changed = true;
      });
      return changed ? next : prev;
    });
  }, [visibleMatchIds]);

  var handleManualApplied = async function () {
    setManualTarget(null);
    await fetchData();
  };

  var handleHighConfidenceApplied = async function () {
    setShowHighConfidenceDialog(false);
    await fetchData();
  };

  var handleReconcileApplied = async function () {
    setReconcileTarget(null);
    await fetchData();
  };

  var handleResidualApplied = async function () {
    setResidualTarget(null);
    await fetchData();
  };

  var totalPages = Math.max(1, Math.ceil(matchTotal / PAGE_SIZE));

  // Sub-tab: Suggestions / Recurring costs / Unreconciled transactions.
  // Reflected in the URL via BankingFilterContext (reconTab), same
  // deep-linkable pattern as the Transactions-tab filters above.
  var activeTab = bankingFilter.reconTab || 'suggestions';
  var handleTabChange = bankingFilter.setReconTab || function () {};

  // Suggestions grouped by entity (canonical name via entityById), each
  // carrying a counterparty->count breakdown for the header line (e.g.
  // "AIS Eurelo (3), ICAB (2)") -- the current page's groups are already
  // server-sorted entity-then-counterparty-then-date (routes/matching.py
  // get_match_details), so a simple first-seen grouping here reproduces
  // that order without re-sorting anything client-side.
  var entityGroups = useMemo(function () {
    var out = [];
    var byKey = {};
    matchDetails.forEach(function (group) {
      var tx = group.transaction;
      var entId = tx ? tx.entity_id : null;
      var key = entId || '__unattributed__';
      if (!byKey[key]) {
        byKey[key] = { key: key, entityId: entId, groups: [], counterpartyCounts: [] };
        out.push(byKey[key]);
      }
      var rec = byKey[key];
      rec.groups.push(group);
      var cp = (tx && tx.counterparty) || 'Unknown';
      var existing = rec.counterpartyCounts.filter(function (c) { return c.name === cp; })[0];
      if (existing) {
        existing.count += 1;
      } else {
        rec.counterpartyCounts.push({ name: cp, count: 1 });
      }
    });
    return out;
  }, [matchDetails]);

  var content = (
      <div className="flex flex-col gap-8" data-testid="reconciliation-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reconciliation</h1>
          <p className="text-muted-foreground mt-1">Match transactions to invoices</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <ArrowLeftRight className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Suggested Matches</p>
                  <p className="text-2xl font-bold">{matchTotal}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="reconciliation-stat-reconciled">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <CheckCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reconciled Transactions</p>
                  <p className="text-2xl font-bold text-green-600">{statsReconciled}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                  <Building2 className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Unreconciled Transactions</p>
                  <p className="text-2xl font-bold text-orange-600">{statsUnreconciled}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                  <FileText className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending Invoices</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {statsPendingInvoices >= 500 ? '500+' : statsPendingInvoices}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sub-tabs: Suggestions / Recurring costs / Unreconciled transactions.
            No functionality lost in the move -- confirm/reject, partial-
            allocation badges, accept-residual, and blocked/failure displays
            all still live where they did, just scoped under their tab. */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="pt-2">
          <TabsList data-testid="reconciliation-tabs">
            <TabsTrigger value="suggestions" data-testid="reconciliation-tab-suggestions">
              Suggestions ({matchTotal})
            </TabsTrigger>
            <TabsTrigger value="recurring" data-testid="reconciliation-tab-recurring">
              Recurring costs ({recurringCostsCount})
            </TabsTrigger>
            <TabsTrigger value="unreconciled" data-testid="reconciliation-tab-unreconciled">
              Unreconciled transactions ({statsUnreconciled})
            </TabsTrigger>
            <TabsTrigger value="receipt-suggestions" data-testid="reconciliation-tab-receipt-suggestions">
              Receipt suggestions ({receiptSuggestedCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="suggestions" className="space-y-6 mt-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <Select value={suggestionsEntityFilter} onValueChange={setSuggestionsEntityFilter}>
                <SelectTrigger className="w-full sm:w-[220px]" data-testid="suggestions-entity-filter-select">
                  <SelectValue placeholder="Entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entities.map(function (e) {
                    return <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* span wrapper: a disabled button's own hover/focus events
                          are unreliable across browsers, so the tooltip trigger
                          wraps it rather than attaching to the button itself. */}
                      <span tabIndex={0}>
                        <Button
                          variant="outline"
                          className="border-green-600 text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          disabled={highConfidenceCandidates.length === 0}
                          onClick={function () { setShowHighConfidenceDialog(true); }}
                          data-testid="confirm-all-high-confidence-btn"
                        >
                          <CheckCheck className="h-4 w-4 mr-2" />
                          Confirm all high-confidence ({highConfidenceCandidates.length})
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {highConfidenceCandidates.length === 0 && (
                      <TooltipContent>
                        <p>
                          No High-tier bulk-confirm candidates right now. See "High-tier near misses"
                          on the Match Funnel (Diagnostics page) for which criterion is blocking each
                          emitted match.
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {selectedMatchIds.size > 0 && (
                  <Button
                    variant="default"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={bulkConfirming}
                    onClick={handleBulkConfirmSelected}
                    data-testid="bulk-confirm-selected-btn"
                  >
                    {bulkConfirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-2" />}
                    Confirm selected ({selectedMatchIds.size})
                  </Button>
                )}
                <AutoMatchChainControl
                  entityId={autoMatchEntityId}
                  onRunCompleted={handleAutoMatchRunCompleted}
                  testIdPrefix="auto-match-btn"
                />
              </div>
            </div>

            <ReconciliationMemoryCard />

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : fetchError ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
                  <h3 className="text-lg font-medium mb-2">Failed to load data</h3>
                  <p className="text-muted-foreground mb-4">{fetchError}</p>
                  <Button onClick={function () { setIsLoading(true); fetchData(); }}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : matchTotal === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ArrowLeftRight className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Pending Matches</h3>
                  <p className="text-muted-foreground mb-4">
                    Run auto-matching to find potential matches between transactions and invoices.
                  </p>
                  <div className="flex justify-center">
                    <AutoMatchChainControl
                      entityId={autoMatchEntityId}
                      onRunCompleted={handleAutoMatchRunCompleted}
                      testIdPrefix="auto-match-empty-state"
                    />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">
                    Suggested Matches ({matchTotal})
                  </h2>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={function () { setPage(function (p) { return p - 1; }); }}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {page} / {totalPages}
                      </span>
                      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={function () { setPage(function (p) { return p + 1; }); }}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {visibleMatchIds.length > 0 && (
                  <div className="flex items-center gap-2" data-testid="suggestions-select-all-row">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={handleToggleSelectAll}
                      aria-label="Select all suggested matches on this page"
                      data-testid="suggestions-select-all-checkbox"
                    />
                    <span className="text-sm text-muted-foreground">
                      {selectedMatchIds.size > 0
                        ? selectedMatchIds.size + ' selected'
                        : 'Select all on this page (' + visibleMatchIds.length + ')'}
                    </span>
                  </div>
                )}

                {matchDetails.length === 0 && matchTotal > 0 && (
                  <Card>
                    <CardContent className="py-6 text-center">
                      <AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {matchTotal} suggested matches exist but none could be loaded for this page.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {entityGroups.map(function (rec) {
                  var ent = rec.entityId ? entityById(rec.entityId) : null;
                  var entName = ent ? ent.name : 'Unattributed';
                  var breakdown = rec.counterpartyCounts.map(function (c) {
                    return c.name + ' (' + c.count + ')';
                  }).join(', ');
                  return (
                    <div key={rec.key} className="space-y-4">
                      <div data-testid={'entity-group-header-' + rec.key}>
                        <h3 className="text-sm font-semibold">{entName}</h3>
                        <p className="text-xs text-muted-foreground" data-testid={'entity-group-counterparties-' + rec.key}>
                          {breakdown}
                        </p>
                      </div>
                      {rec.groups.map(function (group) {
                        var isHighlighted = highlightTxId && group.transaction_id === highlightTxId;
                        return (
                          <TransactionMatchCard
                            key={group.transaction_id}
                            group={group}
                            entityById={entityById}
                            onConfirm={handleConfirm}
                            onReject={handleReject}
                            onRejectAll={handleRejectAll}
                            onManualMatch={setManualTarget}
                            onAcceptResidual={setResidualTarget}
                            highlighted={isHighlighted}
                            cardRef={isHighlighted ? highlightRef : undefined}
                            selectedMatchIds={selectedMatchIds}
                            onToggleSelect={handleToggleSelect}
                          />
                        );
                      })}
                    </div>
                  );
                })}

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={function () { setPage(function (p) { return p - 1; }); }}>
                      <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-3">
                      Page {page} of {totalPages}
                    </span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={function () { setPage(function (p) { return p + 1; }); }}>
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="recurring" className="mt-4">
            <RecurringCostCoverageCard />
          </TabsContent>

          <TabsContent value="receipt-suggestions" className="mt-4" data-testid="receipt-suggestions-section">
            <p className="text-sm text-muted-foreground mb-3">
              Receipt-paid suggestions awaiting your confirm -- a separate population from the
              invoice auto-matcher's Suggestions tab above: these are candidate matches the
              receipt-paid reconciliation pass found for a paid receipt but could not confirm
              automatically (still under review, or ambiguous evidence), so a human click is
              required before they post.
            </p>
            <ReceiptPaidReconciliationPanel />
          </TabsContent>

          <TabsContent value="unreconciled" className="mt-4" data-testid="unreconciled-section">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : fetchError ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
                  <h3 className="text-lg font-medium mb-2">Failed to load data</h3>
                  <p className="text-muted-foreground mb-4">{fetchError}</p>
                  <Button onClick={function () { setIsLoading(true); fetchData(); }}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : unreconciledRows.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">Nothing Unreconciled</h3>
                  <p className="text-muted-foreground">
                    Every transaction without an auto-suggestion has been resolved.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-semibold">
                    Unreconciled Transactions ({unreconciledRows.length})
                  </h2>
                </div>
                {statsUnreconciled > UNRECONCILED_ROW_CAP && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Showing the first {UNRECONCILED_ROW_CAP} of {statsUnreconciled} unreconciled transactions.
                  </p>
                )}
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Counterparty</th>
                            <th className="text-right p-3">Amount</th>
                            <th className="text-right p-3">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unreconciledRows.map(function (tx) {
                            return (
                              <tr key={tx.id} className="border-t" data-testid={'unreconciled-row-' + tx.id}>
                                <td className="p-3 whitespace-nowrap">{tx.date ? format(new Date(tx.date), 'dd MMM yyyy') : '—'}</td>
                                <td className="p-3 truncate max-w-[280px]">{tx.counterparty || tx.description || '—'}</td>
                                <td className={cn('p-3 text-right font-mono', tx.amount >= 0 ? 'text-green-600' : 'text-red-600')}>
                                  {formatCurrency(tx.amount, tx.currency)}
                                </td>
                                <td className="p-3 text-right whitespace-nowrap">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={function () { setManualTarget(tx); }}
                                    data-testid={'manual-match-btn-' + tx.id}
                                  >
                                    <Link2 className="h-3.5 w-3.5 mr-1" /> Match manually
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="ml-2"
                                    onClick={function () { setReconcileTarget(tx); }}
                                    data-testid={'reconcile-no-invoice-btn-' + tx.id}
                                  >
                                    Reconcile without invoice
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <ManualMatchDialog
          transaction={manualTarget}
          open={manualTarget != null}
          onClose={function () { setManualTarget(null); }}
          onApplied={handleManualApplied}
        />
        <ReconcileWithoutInvoiceDialog
          transaction={reconcileTarget}
          open={reconcileTarget != null}
          onClose={function () { setReconcileTarget(null); }}
          onApplied={handleReconcileApplied}
        />
        <AcceptResidualDialog
          invoice={residualTarget}
          open={residualTarget != null}
          onClose={function () { setResidualTarget(null); }}
          onApplied={handleResidualApplied}
        />
        <HighConfidenceBulkConfirmDialog
          open={showHighConfidenceDialog}
          candidates={highConfidenceCandidates}
          onClose={function () { setShowHighConfidenceDialog(false); }}
          onApplied={handleHighConfidenceApplied}
        />
      </div>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
    </DashboardLayout>
  );
}

export function ReconciliationPageContent(props) {
  return <ReconciliationPage embedded {...props} />;
}

export default ReconciliationPage;
